import React from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import '../HeadDoctorDashboard.css';

export default function AppointmentFormModal({
  show,
  copy,
  form,
  setForm,
  doctors,
  editingId,
  saving,
  modalError,
  patientSearch,
  setPatientSearch,
  patientOptions,
  patientSearchLoading,
  onSelectPatient,
  availability,
  onClose,
  onSubmit
}) {
  const isEditing = Boolean(editingId);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="hd-dis-modal-root hd-rdv-form-modal-root"
      backdropClassName="hd-dis-modal-backdrop"
      dialogClassName="hd-dis-modal-dialog"
      contentClassName="hd-dis-modal__content"
      backdrop={saving ? 'static' : true}
      keyboard={!saving}
    >
      <div className="hd-dis-modal__header">
        <div className="hd-dis-modal__header-main">
          <span className="hd-dis-modal__icon" aria-hidden>
            <Icon name="calendar" size={22} />
          </span>
          <div className="hd-dis-modal__titles">
            <span
              className={`hd-dis-modal__mode${
                isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
              }`}
            >
              {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
            </span>
            <h2 className="hd-dis-modal__title">
              {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
            </h2>
            <p className="hd-dis-modal__subtitle mb-0">
              {isEditing ? copy.MODAL_SUBTITLE_EDIT : copy.MODAL_SUBTITLE_NEW}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="hd-dis-modal__close"
          onClick={onClose}
          aria-label={copy.MODAL_CLOSE_ARIA}
        >
          <Icon name="times" size={18} aria-hidden />
        </button>
      </div>

      <Form onSubmit={onSubmit}>
        <div className="hd-dis-modal__body">
          {modalError ? (
            <Alert variant="danger" className="hd-dis-modal__alert mb-0">
              {modalError}
            </Alert>
          ) : null}

          <div className="hd-dis-modal__fields">
            <p className="hd-rdv-modal__section-label">{copy.MODAL_SECTION_SLOT}</p>
            <div className="hd-dis-modal__field-grid hd-rdv-modal__slot-grid">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-rdv-doctor">
                  {copy.FIELD_DOCTOR} *
                </label>
                <select
                  id="hd-rdv-doctor"
                  className="hd-dis-modal__input hd-dis-modal__select"
                  value={form.doctor_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, doctor_id: e.target.value }))}
                  required
                >
                  <option value="">{copy.FIELD_DOCTOR_PLACEHOLDER}</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {`${d.first_name || ''} ${d.last_name || ''}`.trim()} ({d.role})
                    </option>
                  ))}
                </select>
              </div>
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-rdv-start">
                  {copy.FIELD_START} *
                </label>
                <input
                  id="hd-rdv-start"
                  type="datetime-local"
                  className="hd-dis-modal__input"
                  value={form.start_at}
                  onChange={(e) => setForm((prev) => ({ ...prev, start_at: e.target.value }))}
                  required
                />
              </div>
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-rdv-end">
                  {copy.FIELD_END} *
                </label>
                <input
                  id="hd-rdv-end"
                  type="datetime-local"
                  className="hd-dis-modal__input"
                  value={form.end_at}
                  onChange={(e) => setForm((prev) => ({ ...prev, end_at: e.target.value }))}
                  required
                />
              </div>
            </div>

            {availability ? (
              <Alert
                variant={availability.available ? 'success' : 'warning'}
                className="hd-rdv-availability-alert py-2 small mb-0"
              >
                {availability.available ? copy.AVAILABILITY_OK : copy.AVAILABILITY_CONFLICT}
              </Alert>
            ) : null}

            <p className="hd-rdv-modal__section-label">{copy.MODAL_SECTION_PATIENT}</p>
            <div className="hd-dis-modal__field-grid">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-rdv-patient-type">
                  {copy.FIELD_PATIENT_TYPE}
                </label>
                <select
                  id="hd-rdv-patient-type"
                  className="hd-dis-modal__input hd-dis-modal__select"
                  value={form.patient_type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      patient_type: e.target.value,
                      patient_id: ''
                    }))
                  }
                >
                  <option value="SMIE">{copy.PATIENT_SMIE}</option>
                  <option value="EXTERNE">{copy.PATIENT_EXTERNAL}</option>
                </select>
              </div>
              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-dis-modal__label" htmlFor="hd-rdv-patient-search">
                  {copy.FIELD_PATIENT_SEARCH} *
                </label>
                <input
                  id="hd-rdv-patient-search"
                  type="search"
                  className="hd-dis-modal__input"
                  placeholder={copy.FIELD_PATIENT_SEARCH_PLACEHOLDER}
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    setForm((prev) => ({ ...prev, patient_id: '' }));
                  }}
                  autoComplete="off"
                />
                {patientSearchLoading ? (
                  <p className="hd-dis-modal__hint mb-0 mt-1">
                    <Spinner animation="border" size="sm" className="me-1" aria-hidden />
                    {copy.PATIENT_SEARCH_LOADING}
                  </p>
                ) : null}
                {!patientSearchLoading && patientOptions.length > 0 ? (
                  <ul className="hd-rdv-patient-suggest" role="listbox">
                    {patientOptions.map((opt) => (
                      <li key={opt.id}>
                        <button
                          type="button"
                          className="hd-rdv-patient-suggest__item"
                          onClick={() => onSelectPatient(opt)}
                        >
                          {opt.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            <p className="hd-rdv-modal__section-label">{copy.MODAL_SECTION_DETAILS}</p>
            <div className="hd-dis-modal__field">
              <label className="hd-dis-modal__label" htmlFor="hd-rdv-motif">
                {copy.FIELD_MOTIF} *
              </label>
              <input
                id="hd-rdv-motif"
                type="text"
                className="hd-dis-modal__input"
                value={form.motif}
                onChange={(e) => setForm((prev) => ({ ...prev, motif: e.target.value }))}
                placeholder={copy.FIELD_MOTIF_PLACEHOLDER}
                required
              />
            </div>
            <div className="hd-dis-modal__field hd-dis-modal__field--full">
              <label className="hd-dis-modal__label" htmlFor="hd-rdv-notes">
                {copy.FIELD_NOTES}
              </label>
              <textarea
                id="hd-rdv-notes"
                className="hd-dis-modal__textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder={copy.FIELD_NOTES_PLACEHOLDER}
              />
            </div>
          </div>
        </div>

        <div className="hd-dis-modal__footer">
          <button
            type="button"
            className="hd-dis-modal__btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            {copy.BTN_CANCEL}
          </button>
          <button type="submit" className="hd-co-create-btn" disabled={saving || !form.patient_id}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                {copy.BTN_SAVE}
              </>
            ) : isEditing ? (
              copy.BTN_UPDATE
            ) : (
              <>
                <Icon name="plus" size={16} aria-hidden />
                {copy.BTN_CREATE}
              </>
            )}
          </button>
        </div>
      </Form>
    </Modal>
  );
}
